export const PAP_SCRIPT_ID = "pap_x2s6df8d";
export const PAP_SCRIPT_SRC =
  "https://pepdrive365.postaffiliatepro.com/scripts/j13kq5v91y8";
export const PAP_ACCOUNT_ID = "default1";

const PAP_AID_KEY = "pap_a_aid";
const PAP_BID_KEY = "pap_a_bid";
const PAP_DEBUG_KEY = "PAP_DEBUG";
const PAP_REGISTERED_PREFIX = "pap_registered_";

function papDebug(...args) {
  if (typeof window === "undefined") return;
  try {
    const urlDebug =
      new URLSearchParams(window.location.search).get("pap_debug") === "1";
    const sessionDebug = sessionStorage.getItem(PAP_DEBUG_KEY) === "1";
    if (urlDebug || sessionDebug) {
      console.info("[PAP]", ...args);
    }
  } catch {
    /* ignore */
  }
}

function persistAffiliateParams(aAid, aBid) {
  try {
    if (aAid) sessionStorage.setItem(PAP_AID_KEY, aAid);
    if (aBid) sessionStorage.setItem(PAP_BID_KEY, aBid);
  } catch {
    /* ignore */
  }
}

function parseHashAffiliateParams(rawHash) {
  const hash = (rawHash || "").replace(/^#/, "");
  if (!hash) return { aAid: null, aBid: null };

  if (hash.includes("=")) {
    const params = new URLSearchParams(hash.replace(/;/g, "&"));
    return {
      aAid: params.get("a_aid"),
      aBid: params.get("a_bid"),
    };
  }

  return { aAid: hash, aBid: null };
}

/**
 * Promote #a_aid / #a_bid (or shorthand #REF) into the query string and persist
 * affiliate ids for sale fallback.
 */
export function preparePapAffiliateInBrowserUrl() {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  let { aAid, aBid } = parseHashAffiliateParams(url.hash);

  if (!aAid) aAid = url.searchParams.get("a_aid");
  if (!aBid) aBid = url.searchParams.get("a_bid");

  if (aAid || aBid) {
    persistAffiliateParams(aAid, aBid);
    papDebug("persisted affiliate params", { aAid, aBid });
  }

  const hashParams = parseHashAffiliateParams(url.hash);
  if (!hashParams.aAid && !hashParams.aBid) return;

  if (hashParams.aAid) url.searchParams.set("a_aid", hashParams.aAid);
  if (hashParams.aBid) url.searchParams.set("a_bid", hashParams.aBid);
  url.hash = "";

  window.history.replaceState(window.history.state, "", url.toString());
  papDebug("promoted hash affiliate params to query string", url.search);
}

export function loadPostAffScript() {
  if (typeof document === "undefined") {
    return Promise.reject(new Error("No document"));
  }

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(PAP_SCRIPT_ID);

    const resolveIfReady = () => {
      if (window.PostAffTracker) {
        papDebug("PostAffTracker ready");
        resolve();
        return true;
      }
      return false;
    };

    if (resolveIfReady()) return;

    if (existing) {
      if (
        existing.getAttribute("data-pap-loaded") === "1" ||
        existing.readyState === "complete" ||
        existing.readyState === "loaded"
      ) {
        papDebug("PAP script tag already loaded");
        resolve();
        return;
      }

      let settled = false;
      const finish = (fn) => {
        if (settled) return;
        settled = true;
        clearInterval(poll);
        clearTimeout(fallbackTimer);
        fn();
      };

      existing.addEventListener(
        "load",
        () => {
          existing.setAttribute("data-pap-loaded", "1");
          finish(resolve);
        },
        { once: true }
      );
      existing.addEventListener(
        "error",
        () => finish(() => reject(new Error("PAP script failed to load"))),
        { once: true }
      );

      const poll = setInterval(() => {
        if (resolveIfReady()) finish(resolve);
      }, 50);

      const fallbackTimer = setTimeout(() => {
        if (resolveIfReady()) {
          finish(resolve);
          return;
        }
        if (
          existing.getAttribute("data-pap-loaded") === "1" ||
          existing.readyState === "complete" ||
          existing.readyState === "loaded"
        ) {
          finish(resolve);
          return;
        }
        finish(() => reject(new Error("PAP script load timeout")));
      }, 10000);

      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = PAP_SCRIPT_ID;
    script.src = PAP_SCRIPT_SRC;
    script.referrerPolicy = "no-referrer-when-downgrade";
    script.onload = () => {
      script.setAttribute("data-pap-loaded", "1");
      papDebug("PAP script injected and loaded");
      resolve();
    };
    script.onerror = () => reject(new Error("PAP script failed to load"));
    document.head.appendChild(script);
  });
}

export function waitForPostAffTracker(timeoutMs = 10000) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window"));
  }
  if (window.PostAffTracker) return Promise.resolve(window.PostAffTracker);

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const poll = setInterval(() => {
      if (window.PostAffTracker) {
        clearInterval(poll);
        resolve(window.PostAffTracker);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        clearInterval(poll);
        reject(new Error("PostAffTracker unavailable"));
      }
    }, 50);
  });
}

export async function trackPostAffiliateClick() {
  if (typeof window === "undefined") return;

  try {
    await loadPostAffScript();
    await waitForPostAffTracker();

    const hostname = window.location.hostname;
    if (
      hostname === "pepdrive365.com" ||
      hostname.endsWith(".pepdrive365.com")
    ) {
      window.PostAffTracker.setCookieDomain(".pepdrive365.com");
      papDebug("setCookieDomain", ".pepdrive365.com");
    }

    window.PostAffTracker.setAccountId(PAP_ACCOUNT_ID);
    window.PostAffTracker.track();
    papDebug("click tracked");
  } catch (err) {
    papDebug("trackPostAffiliateClick failed", err);
  }
}

function papSaleAlreadyRegistered(orderId) {
  try {
    return !!sessionStorage.getItem(`${PAP_REGISTERED_PREFIX}${orderId}`);
  } catch {
    return false;
  }
}

function markPapSaleRegistered(orderId) {
  try {
    sessionStorage.setItem(`${PAP_REGISTERED_PREFIX}${orderId}`, "1");
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ orderId: string, lineItems: Array<{ productId: string, totalCost: string|number }> }} params
 */
export function registerPostAffiliateSale({ orderId, lineItems }) {
  if (typeof window === "undefined" || !orderId) return false;
  if (papSaleAlreadyRegistered(orderId)) {
    papDebug("sale deduped", orderId);
    return false;
  }
  if (!window.PostAffTracker || !lineItems?.length) return false;

  window.PostAffTracker.setAccountId(PAP_ACCOUNT_ID);

  let pendingAid = null;
  try {
    pendingAid = sessionStorage.getItem(PAP_AID_KEY);
  } catch {
    /* ignore */
  }

  lineItems.forEach(({ productId, totalCost }) => {
    const sale = window.PostAffTracker.createSale();
    sale.setTotalCost(String(totalCost));
    sale.setOrderID(String(orderId));
    sale.setProductID(String(productId));
    if (pendingAid) sale.setAffiliateID(pendingAid);
    papDebug("createSale", { orderId, productId, totalCost, pendingAid });
  });

  window.PostAffTracker.register();
  markPapSaleRegistered(orderId);
  papDebug("sale registered", orderId);
  return true;
}

/**
 * @param {{ orderId: string, lineItems: Array<{ productId: string, totalCost: string|number }> }} params
 */
export async function registerPostAffiliateSaleAsync(params) {
  if (typeof window === "undefined" || !params?.orderId) return false;
  if (papSaleAlreadyRegistered(params.orderId)) {
    papDebug("sale deduped (async)", params.orderId);
    return false;
  }

  try {
    await loadPostAffScript();
    await waitForPostAffTracker();
    return registerPostAffiliateSale(params);
  } catch (err) {
    papDebug("registerPostAffiliateSaleAsync failed", err);
    return false;
  }
}
